import { type ReactNode, useMemo } from 'react';
import { AppRecordsView } from '@/components/apps/AppRecordsView';
import { AppTeamReview } from '@/components/apps/AppTeamReview';
import { AppChartBlock, AppMetricsBlock } from '@/components/apps/AppSpecBlocks';
import { useAppData } from '@/components/apps/useAppData';
import { OutreachSequenceBlock } from '@/components/outreach/OutreachSequenceBlock';
import { toAggregatable } from '@/components/apps/app-records-model';
import type { AppBlock, AppPage } from '@shared/types/app-spec';
import { cn } from '@/lib/utils';

interface AppSpecPageProps {
  appId: string;
  appKey: string;
  canManage: boolean;
  page: AppPage;
  /** The page holding the app's list reports whether the list exists yet (the tabs depend on it). */
  onAvailability?: (available: boolean, title: string | null, hasReviewGrid: boolean) => void;
  /** What an empty list offers (e.g. launching the app). */
  emptyAction?: ReactNode;
  flowId?: string | null;
  /** The app's own header already offers « Importer » (an app that starts from an import). */
  importInHeader?: boolean;
}

/** Figures and charts read the same list as the page (archived objects left out). */
const AggregateBlock = ({ appId, block }: { appId: string; block: Extract<AppBlock, { type: 'metrics' | 'chart' }> }) => {
  const { list, members, error } = useAppData(appId);
  const objects = useMemo(() => list?.records.filter((record) => !record.archivedAt).map((record) => toAggregatable(record, members)) ?? [], [list, members]);
  if (error) return <p role="alert" className="text-sm text-[var(--domino-critical)]">{error}</p>;
  if (!list) return <p role="status" className="text-sm text-domino-inkSoft">Chargement…</p>;
  return block.type === 'metrics'
    ? <AppMetricsBlock block={block} objects={objects} />
    : <AppChartBlock block={block} objects={objects} collection={list.collection} />;
};

/**
 * F6a: one page of an app, drawn from its description: each block of
 * Domino's catalogue in order, full width or side by side. The runs page stays
 * drawn by the tool page itself.
 */
export const AppSpecPage = ({ appId, appKey, canManage, page, onAvailability, emptyAction, flowId, importInHeader = false }: AppSpecPageProps) => {
  const firstRecords = page.blocks.find((block) => block.type === 'records')?.id;
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {page.blocks.map((block) => {
        if (block.type === 'runs') return null;
        const span = cn('min-w-0', block.width === 'half' ? 'lg:col-span-1' : 'lg:col-span-2');
        return (
          <section key={block.id} aria-label={'label' in block && block.label ? block.label : undefined} className={span}>
            {'label' in block && block.label && block.type === 'records' && <h2 className="mb-3 text-sm font-semibold text-domino-ink">{block.label}</h2>}
            {block.type === 'records' && (
              <AppRecordsView appId={appId} appKey={appKey} canManage={canManage} visibleColumns={block.columns} filter={block.filter}
                onAvailability={block.id === firstRecords ? onAvailability : undefined} emptyAction={emptyAction} flowId={flowId} />
            )}
            {block.type === 'team_review' && <AppTeamReview appId={appId} />}
            {block.type === 'sequence' && <OutreachSequenceBlock appId={appId} canManage={canManage} importInHeader={importInHeader} />}
            {(block.type === 'metrics' || block.type === 'chart') && <AggregateBlock appId={appId} block={block} />}
          </section>
        );
      })}
    </div>
  );
};
